import { useCallback, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { DownloadSize, PhotoCard } from '@photos/shared';
import { useDownloads } from '../hooks/useDownloads';
import { useEscape } from '../hooks/useEscape';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { fmtSize } from '../lib/format';
import { AlbumPickerDialog } from './AlbumPicker';
import { useAlbumAction } from '../api/hooks';
import { useSession } from '../state/session';
import { useToast } from '../state/toasts';
import { ShareDialog } from './dialogs/ShareDialog';
import { Icon } from './Icon';

interface SelectionBarProps {
  photos: PhotoCard[];
  onClear(): void;
  /** Studio actions shown after Download (the Browse bar has none). */
  children?: ReactNode;
  /** Studio has its own share rules per album; Browse shows Share link to people who can share. */
  share?: boolean;
  /** Trash: trashed photos can't be downloaded. */
  canDownload?: boolean;
}

type DialogKind = 'share' | 'glean' | null;

/**
 * Floating bar for the current selection (prototype `syncSel` + `#selbar`). In Browse: Download, Share link and, for
 * Flock photos, Add to Glean. Studio passes its own actions as children.
 */
export function SelectionBar({ photos, onClear, children, share, canDownload = true }: SelectionBarProps) {
  const { access } = useSession();
  const toast = useToast();
  const addToGlean = useAlbumAction();
  const { downloadZip } = useDownloads();
  const [menu, setMenu] = useState(false);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const dd = useRef<HTMLDivElement>(null);
  const menuEl = useRef<HTMLDivElement>(null);
  const refs = useRef([dd, menuEl]).current;
  // The bar scrolls horizontally on small screens (overflow), which would clip a menu inside it, so the menu is
  // rendered in a fixed layer anchored to the Download button.
  const [anchor, setAnchor] = useState<{ left: number; bottom: number } | null>(null);
  const toggleMenu = () => {
    const r = dd.current?.getBoundingClientRect();
    if (r) setAnchor({ left: r.left, bottom: window.innerHeight - r.top });
    setMenu((m) => !m);
  };
  const closeMenu = useCallback(() => setMenu(false), []);
  useOutsideClick(refs, closeMenu, menu);
  useEscape(closeMenu, menu);

  const n = photos.length;
  const bytes = photos.reduce((a, p) => a + p.bytes, 0);
  // Share links are for released photos; the dialog explains when the selection has others.
  const canShare = share ?? access.capabilities.share;
  const flock = children ? [] : photos.filter((p) => p.source === 'flock');
  const canAddToGlean = flock.length > 0 && access.isMedia;

  const promote = (album: { id: string; name: string }) =>
    addToGlean.mutate(
      { action: 'glean', albumId: album.id, ids: flock.map((p) => p.id) },
      {
        onSuccess: (r) => {
          setDialog(null);
          toast({ icon: 'folder', title: `Added ${r.changed} Flock photos to ${album.name}`, sub: 'They keep their tags and the poster’s credit.' });
          onClear();
        },
        onError: (e) => toast({ icon: 'info', title: 'That didn’t work', sub: e.message }),
      },
    );

  const download = (size: DownloadSize) => {
    setMenu(false);
    void downloadZip(photos, size);
  };

  const size = fmtSize(bytes);
  return (
    <>
      <div className={`selbar${n ? ' open' : ''}`} id="selbar" aria-hidden={!n}>
        {n > 0 && (
          <>
            <span className="count">
              {n} selected<small>{size}</small>
            </span>
{canDownload && (
            <div className="dropdown" ref={dd}>
              <button type="button" className="btn primary" aria-haspopup="menu" aria-expanded={menu} onClick={toggleMenu}>
                <Icon name="dl" />
                Download
                <Icon name="chev" />
              </button>
            </div>
            )}
            {canShare && (
              <button type="button" className="btn" onClick={() => setDialog('share')}>
                <Icon name="share" />
                Share link
              </button>
            )}
            {canAddToGlean && (
              <button type="button" className="btn" onClick={() => setDialog('glean')}>
                <Icon name="folder" />
                Add to Glean
              </button>
            )}
            {children}
            <span className="sep" />
            <button type="button" className="btn" aria-label="Clear selection" onClick={onClear}>
              <Icon name="x" />
            </button>
          </>
        )}
      </div>
      {menu &&
        anchor &&
        n > 0 &&
        createPortal(
          <div className="dropdown" ref={menuEl} style={{ position: 'fixed', left: anchor.left, bottom: anchor.bottom, zIndex: 40 }}>
              <div className="menu open" role="menu">
                <div className="lbl">Download {n} as ZIP</div>
                <button type="button" role="menuitem" onClick={() => download('original')}>
                  <Icon name="image" />
                  <span>
                    Originals
                    <br />
                    <small style={{ color: 'var(--muted)' }}>Full quality · {size}</small>
                  </span>
                </button>
                <button type="button" role="menuitem" onClick={() => download('large')}>
                  <Icon name="image" />
                  <span>
                    Large · 2048px
                    <br />
                    <small style={{ color: 'var(--muted)' }}>For slides &amp; bulletins · ~{Math.round(n * 0.9)} MB</small>
                  </span>
                </button>
                <button type="button" role="menuitem" onClick={() => download('social')}>
                  <Icon name="image" />
                  <span>
                    Social · 1080px
                    <br />
                    <small style={{ color: 'var(--muted)' }}>For Instagram &amp; messages · ~{Math.round(n * 0.35)} MB</small>
                  </span>
                </button>
              </div>
          </div>,
          document.body,
        )}
      {dialog === 'share' && <ShareDialog photos={photos} onClose={() => setDialog(null)} />}
      {dialog === 'glean' && (
        <AlbumPickerDialog
          title={`Add ${flock.length} Flock ${flock.length === 1 ? 'photo' : 'photos'} to Glean`}
          help="Choose the album they belong in. They stay released and keep their tags and the poster’s credit."
          confirmLabel="Add to Glean"
          allowCreate={access.isMedia}
          busy={addToGlean.isPending}
          onPick={promote}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  );
}
